import type React from 'react';
import { useCallback, useId, useMemo, useState } from 'react';
import { useWordsHappenTheme } from '../../theme';
import styles from './tabs.module.scss';
import { WordsHappenTabsContext } from './tabs-context';

export interface WordsHappenTabsProps {
  children?: React.ReactNode;
  className?: string;
  /** The selected tab, controlled. Give `onChange` too, e.g. to follow the route. */
  value?: string;
  /** The tab selected first, uncontrolled. */
  defaultValue?: string;
  /** Called with a newly chosen tab; never for the one already selected. */
  onChange?: (value: string) => void;
}

/**
 * Tabs: a WordsHappenTabList of WordsHappenTab, and a WordsHappenTabPanel for each. Controlled with `value` and
 * `onChange` (e.g. one tab per route), or uncontrolled with `defaultValue`.
 *
 * Copied from fiber's Tabs and restyled; horizontal only, without fiber's sizes or scroll arrows.
 */
export const WordsHappenTabs: React.FC<WordsHappenTabsProps> = ({
  children,
  className,
  value,
  defaultValue,
  onChange,
}) => {
  const { theme } = useWordsHappenTheme();
  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = useState(defaultValue ?? '');
  const activeValue = isControlled ? value : internalValue;
  const baseId = useId();

  const setActiveValue = useCallback(
    (next: string) => {
      if (next === activeValue) {
        return;
      }
      if (!isControlled) {
        setInternalValue(next);
      }
      onChange?.(next);
    },
    [activeValue, isControlled, onChange],
  );

  const context = useMemo(() => ({ activeValue, baseId, setActiveValue }), [activeValue, baseId, setActiveValue]);

  const cls = [styles.tabs, theme === 'meadow' ? styles.meadow : styles.flat, className].filter(Boolean).join(' ');

  return (
    <WordsHappenTabsContext value={context}>
      <div className={cls}>{children}</div>
    </WordsHappenTabsContext>
  );
};
